import * as React from "react";
import { Portal } from "radix-ui";

import { Elevated } from "../elevated";
import { cn } from "../utils";

/**
 * The floating tip a chart raises under the cursor — cursor-positioned, flipped
 * above the pointer when there is room, clamped inside the viewport, and faded
 * out over 160ms so it sinks away instead of blinking. Promoted from the
 * `/styleguide` workflow prototypes (`app/styleguide/_card-strip.tsx`): more
 * than one chart needs it, and a second copy is how two charts start behaving
 * differently.
 */
export function GraphTip({
  x,
  y,
  open,
  children,
}: {
  x: number;
  y: number;
  open: boolean;
  children: React.ReactNode;
}) {
  const HALF = 96;
  const GAP = 12;
  const EST_H = 60;
  const vw = typeof window !== "undefined" ? window.innerWidth : x + HALF;
  const left = Math.min(Math.max(x, HALF), vw - HALF);
  const roomAbove = y - GAP - EST_H >= 0;
  const top = roomAbove ? y - GAP : y + GAP;
  const transform = roomAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)";
  return (
    <Portal.Root>
      <div className="pointer-events-none fixed z-50" style={{ left, top, transform }}>
        <Elevated offset={2} shadowLevel={3} asChild>
          <div
            role="tooltip"
            data-open={open}
            className={cn(
              "w-max rounded-md px-2.5 py-1.5 text-left transition-[opacity,transform] duration-150 ease-out",
              "data-[open=true]:opacity-100 data-[open=true]:scale-100 data-[open=false]:opacity-0 data-[open=false]:scale-95",
              roomAbove ? "origin-bottom" : "origin-top",
            )}
          >
            {children}
          </div>
        </Elevated>
      </div>
    </Portal.Root>
  );
}

/**
 * The hover state a cursor-tracked chart needs: which index is under the
 * pointer, where the pointer is, and a lingering copy that survives 160ms past
 * mouse-out so the tip can fade rather than vanish.
 *
 * Returns `bind` for the plot element and `shown` for rendering. Extracted
 * because getting the linger right is fiddly and every chart must get it
 * identically.
 */
export function useGraphHover(count: number) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [hover, setHover] = React.useState<{ i: number; x: number; y: number } | null>(null);
  const [shown, setShown] = React.useState<{ i: number; x: number; y: number; open: boolean } | null>(
    null,
  );

  React.useEffect(() => {
    if (hover) {
      setShown({ ...hover, open: true });
      return;
    }
    setShown((p) => (p && p.open ? { ...p, open: false } : p));
    const t = window.setTimeout(() => setShown(null), 160);
    return () => window.clearTimeout(t);
  }, [hover]);

  const onMouseMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el || count === 0) return;
    const rect = el.getBoundingClientRect();
    const rel = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
    const i = Math.max(0, Math.min(count - 1, Math.round(rel * (count - 1))));
    setHover({ i, x: e.clientX, y: e.clientY });
  };

  return {
    shown,
    bind: { ref, onMouseMove, onMouseLeave: () => setHover(null) },
  } as const;
}
